import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Mail, Plus, Send, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox as UntypedCheckbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/DataTable';
import { useConfirmDialog } from '@/components/shared/ConfirmDialog';
import PageHeader from '@/components/vf/PageHeader';
import { useOrganisations } from '@/hooks/queries/useAdmin';
import { useCursorPages } from '@/hooks/useCursorPages';
import {
  BROADCAST_TYPES, deleteBroadcast, listBroadcastPage, sendBroadcast,
  type Broadcast, type BroadcastInput, type BroadcastType,
} from '@/api/endpoints/backoffice';
import { showErrorToast, toUserMessage } from '@/lib/errorToast';
import { ApiErrorException } from '@/api/client';
import { BRAND } from '@/config/brand';

/**
 * /AdminNotifications — notifications broadcast to the organisations' client
 * inboxes, super admin only (W6, B3-AdminNotifications; QW-h9: in
 * SUPER_ADMIN_ONLY_PAGES).
 *
 *   - write, then PREVIEW (the inbox card and the email as the client gets
 *     them) and CONFIRM « Envoyer à N organisations »: a broadcast cannot be
 *     taken back from the inboxes once read;
 *   - the server sends (W6-NOTIF-1: the page used to write to an entity the
 *     server refuses, and to send the emails itself one by one);
 *   - the history pages by server cursor with the total, and gives each
 *     broadcast's status: recipients, read count, email status (refreshed
 *     while emails are leaving); deletion asks first (DES-13).
 */

const PAGE_SIZE = 25;
// The shadcn Checkbox is an untyped (@ts-nocheck) forwardRef.
const Checkbox = UntypedCheckbox as unknown as (props: { checked: boolean; onCheckedChange: () => void }) => JSX.Element;

const EMPTY: BroadcastInput = { title: '', message: '', type: 'info', target_type: 'all', organisation_ids: [], send_email: false };

export default function AdminNotifications() {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const qc = useQueryClient();
  const orgs = useOrganisations();
  const activeOrgs = useMemo(() => (orgs.data?.organisations ?? []).filter((o) => o.is_active), [orgs.data]);
  const orgName = useMemo(() => new Map((orgs.data?.organisations ?? []).map((o) => [o.id, o.company_name])), [orgs.data]);
  const [composeOpen, setComposeOpen] = useState(false);
  const [ask, confirmDialog] = useConfirmDialog();

  const { query, page, onPageChange } = useCursorPages<Broadcast>(
    ['admin', 'broadcasts'],
    (cursor, signal) => listBroadcastPage({ limit: PAGE_SIZE, cursor }, signal),
    // Emails leave in the background: follow them until they are done.
    { refetchInterval: (data) => (data?.items.some((b) => b.email_status === 'sending') ? 5000 : false) },
  );

  const remove = useMutation({
    mutationFn: (id: string) => deleteBroadcast(id),
    onSuccess: () => { toast.success(t('admin:notificationsBroadcast.history.deleted')); void qc.invalidateQueries({ queryKey: ['admin', 'broadcasts'] }); },
    onError: (err) => showErrorToast(err),
  });

  const askDelete = async (b: Broadcast) => {
    const ok = await ask({
      title: t('admin:notificationsBroadcast.history.deleteTitle', { title: b.title }),
      description: t('admin:notificationsBroadcast.history.deleteDescription', { count: b.recipients }),
      confirmLabel: t('common:actions.delete'),
    });
    if (ok) remove.mutate(b.id);
  };

  const typeLabel = (type: BroadcastType) => t(`admin:notificationsBroadcast.typeLabels.${type}`);
  const fmt = (iso: string) => new Date(iso).toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' });

  const columns: DataTableColumn<Broadcast>[] = [
    { id: 'date', header: t('admin:notificationsBroadcast.history.columns.date'), cell: (b) => <span className="whitespace-nowrap">{fmt(b.created_date)}</span> },
    {
      id: 'title', header: t('admin:notificationsBroadcast.history.columns.notification'),
      cell: (b) => (
        <span className="flex max-w-md flex-col">
          <span className="font-medium">{b.title}</span>
          <span className="line-clamp-1 text-xs text-muted-foreground">{b.message}</span>
        </span>
      ),
    },
    { id: 'type', header: t('admin:notificationsBroadcast.history.columns.type'), hideable: true, cell: (b) => <Badge variant="outline">{typeLabel(b.type)}</Badge> },
    {
      id: 'recipients', header: t('admin:notificationsBroadcast.history.columns.recipients'),
      cell: (b) => (b.target_type === 'all'
        ? t('admin:notificationsBroadcast.history.allOrganisations', { count: b.recipients })
        : <span title={b.organisation_ids.map((id) => orgName.get(id) ?? id).join(', ')}>{t('admin:notificationsBroadcast.history.organisationCount', { count: b.recipients })}</span>),
    },
    {
      id: 'read', header: t('admin:notificationsBroadcast.history.columns.read'), align: 'right',
      // DES-19: no recipient, no rate.
      cell: (b) => (b.recipients > 0 ? <span className="tabular-nums">{b.read_count} / {b.recipients}</span> : '—'),
    },
    { id: 'email', header: t('admin:notificationsBroadcast.history.columns.email'), cell: (b) => <EmailStatus b={b} /> },
    {
      id: 'actions', header: <span className="sr-only">{t('admin:notificationsBroadcast.history.columns.actions')}</span>, align: 'right',
      cell: (b) => (
        <Button variant="ghost" size="icon" className="h-11 w-11" aria-label={t('admin:notificationsBroadcast.history.deleteAria', { title: b.title })} onClick={() => void askDelete(b)}>
          <Trash2 className="size-4 text-destructive" />
        </Button>
      ),
    },
  ];

  const err = query.error;
  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-5">
      <PageHeader
        as="h1"
        title={t('admin:notifications.title')}
        subtitle={t('admin:notificationsBroadcast.subtitle')}
        actions={
          <Button className="gap-2" onClick={() => setComposeOpen(true)}>
            <Plus className="size-4" /> {t('admin:notificationsBroadcast.new')}
          </Button>
        }
      />

      <p className="text-sm text-muted-foreground">
        {orgs.isError
          ? t('admin:notificationsBroadcast.activeOrganisationsUnavailable')
          : orgs.data ? t('admin:notificationsBroadcast.activeOrganisations', { count: activeOrgs.length }) : ' '}
      </p>

      <section aria-labelledby="broadcast-history" className="space-y-2">
        <h2 id="broadcast-history" className="text-base font-semibold">{t('admin:notificationsBroadcast.history.title')}</h2>
        <DataTable<Broadcast>
          caption={t('admin:notificationsBroadcast.history.title')}
          storageKey="admin-broadcasts"
          columns={columns}
          rows={query.data?.items ?? []}
          getRowId={(b) => b.id}
          status={query.isLoading ? 'loading' : query.isError && !query.data ? 'error' : 'ready'}
          error={err ? { message: toUserMessage(err), code: err instanceof ApiErrorException ? err.code : undefined, onRetry: () => void query.refetch() } : undefined}
          empty={{
            title: t('admin:notificationsBroadcast.history.empty'),
            action: <Button size="sm" onClick={() => setComposeOpen(true)}>{t('admin:notificationsBroadcast.new')}</Button>,
          }}
          pagination={{ page, pageSize: PAGE_SIZE, total: query.data?.total ?? null, onPageChange }}
        />
      </section>

      {confirmDialog}
      <ComposeDialog
        open={composeOpen}
        onOpenChange={setComposeOpen}
        organisations={activeOrgs.map((o) => ({ id: o.id, name: o.company_name, email: o.email }))}
        onSent={() => { setComposeOpen(false); void qc.invalidateQueries({ queryKey: ['admin', 'broadcasts'] }); }}
      />
    </div>
  );
}

function EmailStatus({ b }: { b: Broadcast }) {
  const { t } = useTranslation(['admin']);
  const k = 'admin:notificationsBroadcast.emailStatus';
  switch (b.email_status) {
    case 'none': return <span className="text-muted-foreground">{t(`${k}.none`)}</span>;
    case 'sending': return <Badge variant="outline">{t(`${k}.sending`)}</Badge>;
    case 'sent': return <Badge variant="default" className="gap-1"><Mail className="size-3" aria-hidden />{t(`${k}.sent`, { count: b.emails_sent })}</Badge>;
    case 'partial': return <Badge variant="destructive">{t(`${k}.partial`, { sent: b.emails_sent, total: b.emails_sent + b.emails_failed })}</Badge>;
    case 'failed': return <Badge variant="destructive">{t(`${k}.failed`)}</Badge>;
    case 'unavailable': return <Badge variant="secondary" title={t(`${k}.unavailableHint`)}>{t(`${k}.unavailable`)}</Badge>;
  }
}

/** Write → preview → confirm. Nothing leaves before the explicit « Envoyer ». */
function ComposeDialog({ open, onOpenChange, organisations, onSent }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organisations: Array<{ id: string; name: string; email: string }>;
  onSent: () => void;
}) {
  const { t } = useTranslation(['admin', 'common']);
  const [form, setForm] = useState<BroadcastInput>(EMPTY);
  const [step, setStep] = useState<'write' | 'preview'>('write');
  const [filter, setFilter] = useState('');
  const send = useMutation({ mutationFn: (input: BroadcastInput) => sendBroadcast(input) });

  const recipients = form.target_type === 'all' ? organisations.length : (form.organisation_ids ?? []).length;
  const canPreview = form.title.trim() && form.message.trim() && recipients > 0;
  const shown = organisations.filter((o) => !filter.trim() || o.name.toLowerCase().includes(filter.trim().toLowerCase()));

  const close = (next: boolean) => {
    if (!next) { setForm(EMPTY); setStep('write'); setFilter(''); }
    onOpenChange(next);
  };

  const toggleOrg = (id: string) => setForm((f) => {
    const ids = f.organisation_ids ?? [];
    return { ...f, organisation_ids: ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id] };
  });

  const submit = async () => {
    try {
      const b = await send.mutateAsync({
        ...form,
        title: form.title.trim(),
        message: form.message.trim(),
        organisation_ids: form.target_type === 'specific' ? form.organisation_ids : undefined,
      });
      toast.success(t('admin:notificationsBroadcast.sent', { count: b.recipients }));
      close(false);
      onSent();
    } catch (err) {
      // The draft stays: correct, then send again.
      showErrorToast(err);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{step === 'write' ? t('admin:notificationsBroadcast.dialog.title') : t('admin:notificationsBroadcast.preview.title')}</DialogTitle>
          <DialogDescription>
            {step === 'write' ? t('admin:notificationsBroadcast.dialog.description') : t('admin:notificationsBroadcast.preview.description')}
          </DialogDescription>
        </DialogHeader>

        {step === 'write' ? (
          <form
            className="space-y-4"
            onSubmit={(e) => { e.preventDefault(); if (canPreview) setStep('preview'); }}
          >
            <div className="space-y-2">
              <Label htmlFor="bc-title">{t('admin:notificationsBroadcast.dialog.titleLabel')}</Label>
              <Input id="bc-title" maxLength={200} required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t('admin:notificationsBroadcast.dialog.titlePlaceholder')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bc-message">{t('admin:notificationsBroadcast.dialog.messageLabel')}</Label>
              <Textarea id="bc-message" rows={4} maxLength={5000} required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={t('admin:notificationsBroadcast.dialog.messagePlaceholder')} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>{t('admin:notificationsBroadcast.dialog.typeLabel')}</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v as BroadcastType })}>
                  <SelectTrigger aria-label={t('admin:notificationsBroadcast.dialog.typeLabel')}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BROADCAST_TYPES.map((type) => <SelectItem key={type} value={type}>{t(`admin:notificationsBroadcast.typeLabels.${type}`)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('admin:notificationsBroadcast.dialog.recipientsLabel')}</Label>
                <Select value={form.target_type} onValueChange={(v) => setForm({ ...form, target_type: v as 'all' | 'specific', organisation_ids: [] })}>
                  <SelectTrigger aria-label={t('admin:notificationsBroadcast.dialog.recipientsLabel')}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('admin:notificationsBroadcast.dialog.allOrganisations', { count: organisations.length })}</SelectItem>
                    <SelectItem value="specific">{t('admin:notificationsBroadcast.dialog.specificOrganisations')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.target_type === 'specific' && (
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium">{t('admin:notificationsBroadcast.dialog.selectOrganisations')}</legend>
                <Input type="search" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={t('admin:notificationsBroadcast.dialog.searchOrganisation')} aria-label={t('admin:notificationsBroadcast.dialog.searchOrganisation')} />
                <div className="max-h-48 space-y-1 overflow-y-auto rounded-lg border p-2">
                  {shown.map((o) => (
                    <label key={o.id} className="flex min-h-11 cursor-pointer items-center gap-3 rounded px-2 hover:bg-muted">
                      <Checkbox checked={(form.organisation_ids ?? []).includes(o.id)} onCheckedChange={() => toggleOrg(o.id)} />
                      <span className="flex flex-col">
                        <span className="text-sm font-medium">{o.name}</span>
                        <span className="text-xs text-muted-foreground">{o.email}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
            )}
            <label className="flex min-h-11 items-center justify-between gap-3 rounded-lg border px-3">
              <span className="flex items-center gap-2 text-sm"><Mail className="size-4 text-muted-foreground" aria-hidden />{t('admin:notificationsBroadcast.dialog.alsoEmail')}</span>
              <Switch checked={form.send_email} onCheckedChange={(v) => setForm({ ...form, send_email: v })} aria-label={t('admin:notificationsBroadcast.dialog.alsoEmail')} />
            </label>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => close(false)}>{t('common:actions.cancel')}</Button>
              <Button type="submit" disabled={!canPreview}>{t('admin:notificationsBroadcast.preview.open')}</Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-4">
            <section aria-label={t('admin:notificationsBroadcast.preview.inbox')} className="space-y-1">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('admin:notificationsBroadcast.preview.inbox')}</p>
              <div className="rounded-lg border p-3" data-testid="broadcast-preview-inbox">
                <div className="flex items-center gap-2">
                  <p className="font-semibold">{form.title.trim()}</p>
                  <Badge variant="outline">{t(`admin:notificationsBroadcast.typeLabels.${form.type}`)}</Badge>
                </div>
                <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">{form.message.trim()}</p>
              </div>
            </section>
            {form.send_email && (
              <section aria-label={t('admin:notificationsBroadcast.preview.email')} className="space-y-1">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('admin:notificationsBroadcast.preview.email')}</p>
                <div className="rounded-lg border p-3 text-sm" data-testid="broadcast-preview-email">
                  <p><span className="text-muted-foreground">{t('admin:notificationsBroadcast.preview.subject')}</span> [{BRAND.name}] {form.title.trim()}</p>
                  <p className="mt-2 whitespace-pre-line">{form.message.trim()}</p>
                  <p className="mt-2 text-muted-foreground">— {t('admin:notificationsBroadcast.preview.signature', { brand: BRAND.name })}</p>
                </div>
              </section>
            )}
            <p className="text-sm">
              {form.target_type === 'all'
                ? t('admin:notificationsBroadcast.preview.toAll', { count: recipients })
                : t('admin:notificationsBroadcast.preview.toSome', { count: recipients, names: organisations.filter((o) => form.organisation_ids?.includes(o.id)).map((o) => o.name).join(', ') })}
              {form.send_email ? ` ${t('admin:notificationsBroadcast.preview.withEmail')}` : ''}
            </p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setStep('write')}>{t('admin:notificationsBroadcast.preview.back')}</Button>
              <Button type="button" className="gap-2" disabled={send.isPending} onClick={() => void submit()}>
                <Send className="size-4" /> {t('admin:notificationsBroadcast.preview.confirm', { count: recipients })}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
